import { PERMISSION } from './keys';
import { RoleSet, atLeast, allOf, NONE } from './builders';
import { ROLES } from '@/utils/constants';

export interface Rule {
	nonPos: RoleSet; // access on non-POS-integrated locations
	pos: RoleSet; // access on POS-integrated locations
	stopAdminBypass?: boolean; // Set to true to stop admin from bypassing this rule (e.g. session-identity keys).
}

// SOURCE OF TRUTH: https://docs.google.com/spreadsheets/d/11z5OTfQDK2Uawxr-1k144Q0gkBGthxiuEq1EMYJ9u3M
export const RULES: Record<PERMISSION, Rule> = {
	// --- Menu components ---
	[PERMISSION.MENU_NAMES]:              { nonPos: atLeast(ROLES.supervisor), pos: allOf(ROLES.enterprise) },
	[PERMISSION.MENU_IMAGES]:             { nonPos: atLeast(ROLES.supervisor), pos: allOf(ROLES.enterprise) },
	[PERMISSION.MENU_AVAILABILITY]:       { nonPos: atLeast(ROLES.supervisor), pos: NONE },
	[PERMISSION.MENU_TYPE]:               { nonPos: atLeast(ROLES.lead), pos: atLeast(ROLES.lead) },
	[PERMISSION.MENU_DESCRIPTIONS]:       { nonPos: atLeast(ROLES.supervisor), pos: allOf(ROLES.enterprise) },
	[PERMISSION.MENU_ADVANCED_SETTINGS]:  { nonPos: atLeast(ROLES.manager), pos: atLeast(ROLES.manager) },
	[PERMISSION.MENU_IMPORT]:             { nonPos: atLeast(ROLES.lead), pos: atLeast(ROLES.lead) },

	// --- Edit functions ---
	[PERMISSION.MENU_ARCHIVE]:            { nonPos: atLeast(ROLES.lead), pos: NONE },
	[PERMISSION.MENU_EDIT]:               { nonPos: atLeast(ROLES.supervisor), pos: atLeast(ROLES.member) },
	[PERMISSION.MENU_DUPLICATE]:          { nonPos: atLeast(ROLES.supervisor), pos: NONE },
	[PERMISSION.MENU_DELETE]:             { nonPos: atLeast(ROLES.manager), pos: NONE },
	[PERMISSION.MENU_EXPORT]:             { nonPos: atLeast(ROLES.supervisor), pos: atLeast(ROLES.member) },

	// --- Sections ---
	[PERMISSION.SECTION_EDIT]:            { nonPos: atLeast(ROLES.supervisor), pos: atLeast(ROLES.member) },
	[PERMISSION.SECTION_NAMES]:           { nonPos: atLeast(ROLES.supervisor), pos: allOf(ROLES.enterprise) },
	[PERMISSION.SECTION_DESCRIPTIONS]:    { nonPos: atLeast(ROLES.supervisor), pos: allOf(ROLES.enterprise) },

	// --- Edit item ---
	[PERMISSION.ITEM_NAMES]:              { nonPos: atLeast(ROLES.supervisor), pos: allOf(ROLES.enterprise) },
	[PERMISSION.ITEM_IMAGES]:             { nonPos: atLeast(ROLES.supervisor), pos: allOf(ROLES.enterprise) },
	[PERMISSION.ITEM_OUT_OF_STOCK]:       { nonPos: atLeast(ROLES.member), pos: NONE },
	[PERMISSION.ITEM_FEATURED]:           { nonPos: atLeast(ROLES.lead), pos: atLeast(ROLES.lead) },
	[PERMISSION.ITEM_UPSELL]:             { nonPos: atLeast(ROLES.lead), pos: atLeast(ROLES.lead) },
	[PERMISSION.ITEM_MEAL_PACKAGE]:       { nonPos: atLeast(ROLES.manager), pos: atLeast(ROLES.manager) },
	[PERMISSION.ITEM_MIN_ORDER_QTY]:      { nonPos: atLeast(ROLES.lead), pos: atLeast(ROLES.lead) },
	[PERMISSION.ITEM_ASSIGN_TAGS]:        { nonPos: atLeast(ROLES.supervisor), pos: NONE },
	[PERMISSION.ITEM_PRICING]:            { nonPos: atLeast(ROLES.supervisor), pos: NONE },
	[PERMISSION.ITEM_PRICE_OPTIONS]:      { nonPos: atLeast(ROLES.lead), pos: atLeast(ROLES.lead) },
	[PERMISSION.ITEM_PRICE_ON_REQUEST]:   { nonPos: atLeast(ROLES.lead), pos: atLeast(ROLES.lead) },
	[PERMISSION.ITEM_NUTRITION_SHEET]:    { nonPos: atLeast(ROLES.manager), pos: atLeast(ROLES.manager) },
	[PERMISSION.ITEM_ALLERGENS]:          { nonPos: atLeast(ROLES.supervisor), pos: NONE },
	[PERMISSION.ITEM_DIETS]:              { nonPos: atLeast(ROLES.supervisor), pos: NONE },
	[PERMISSION.ITEM_DESCRIPTIONS]:       { nonPos: atLeast(ROLES.supervisor), pos: allOf(ROLES.enterprise) },
	[PERMISSION.ITEM_ACCOUNTING_CATEGORY]:{ nonPos: atLeast(ROLES.supervisor), pos: NONE },
	[PERMISSION.ITEM_HST_REBATE]:         { nonPos: atLeast(ROLES.manager), pos: NONE },
	[PERMISSION.ITEM_TAX_RATES]:          { nonPos: atLeast(ROLES.manager), pos: NONE },
	[PERMISSION.ITEM_PRICE_SCHEDULING]:   { nonPos: atLeast(ROLES.lead), pos: atLeast(ROLES.lead) },

	// --- Modifiers ---
	[PERMISSION.MODIFIERS]:               { nonPos: atLeast(ROLES.supervisor), pos: NONE },
	[PERMISSION.MODIFIER_GROUPS]:         { nonPos: atLeast(ROLES.supervisor), pos: NONE },

	// --- Replicate ---
	[PERMISSION.REPLICATE]:               { nonPos: atLeast(ROLES.supervisor), pos: NONE },
	[PERMISSION.REPLICATE_DAILY_OVERWRITE]: { nonPos: atLeast(ROLES.lead), pos: atLeast(ROLES.lead) },

	// --- Tags ---
	[PERMISSION.CREATE_TAGS]:             { nonPos: atLeast(ROLES.lead), pos: atLeast(ROLES.lead) },

	// --- Availability tab ---
	[PERMISSION.AVAILABILITY_HOURS]:      { nonPos: atLeast(ROLES.member), pos: atLeast(ROLES.member) },
	[PERMISSION.AVAILABILITY_CLOSED_DATES]: { nonPos: atLeast(ROLES.member), pos: atLeast(ROLES.member) },

	// --- Orders ---
	[PERMISSION.ORDER_PAUSE]:             { nonPos: atLeast(ROLES.lead), pos: atLeast(ROLES.lead) },
	[PERMISSION.ORDER_REFUND]:            { nonPos: atLeast(ROLES.manager), pos: atLeast(ROLES.manager) },
	[PERMISSION.ORDER_REFUND_REQUEST]:    { nonPos: allOf(ROLES.lead), pos: allOf(ROLES.lead) },
	[PERMISSION.ORDER_REQUEST_DEPOSIT]:   { nonPos: atLeast(ROLES.lead), pos: atLeast(ROLES.lead) },
	[PERMISSION.ORDER_MODIFY]:            { nonPos: atLeast(ROLES.lead), pos: atLeast(ROLES.lead) },
	[PERMISSION.ORDER_CREATE_CATERING]:   { nonPos: atLeast(ROLES.lead), pos: atLeast(ROLES.lead) },
	[PERMISSION.ORDER_CANCEL_CATERING]:   { nonPos: atLeast(ROLES.manager), pos: atLeast(ROLES.manager) },
	[PERMISSION.ORDER_CANCEL_SCHOOL_REFUND]: { nonPos: atLeast(ROLES.member), pos: atLeast(ROLES.member) },
	[PERMISSION.ORDER_IMPORT_SCHOOL]:     { nonPos: atLeast(ROLES.lead), pos: atLeast(ROLES.lead) },

	// --- Orders summary ---
	[PERMISSION.ORDERS_REPORT]:           { nonPos: atLeast(ROLES.member), pos: atLeast(ROLES.member) },

	// --- Settings > Operations ---
	[PERMISSION.SET_CUSTOM_QUESTIONS]:    { nonPos: atLeast(ROLES.lead), pos: NONE },
	[PERMISSION.EDIT_INQUIRY_FORM]: 	  { nonPos: atLeast(ROLES.lead), pos: atLeast(ROLES.lead) },

	// --- Settings > Fulfillment (pos preserves current manager-level tab gate) ---
	[PERMISSION.SET_DELIVERY]:            { nonPos: atLeast(ROLES.lead), pos: atLeast(ROLES.manager) },
	[PERMISSION.SET_ORDERING_LOCATIONS]:  { nonPos: atLeast(ROLES.manager), pos: atLeast(ROLES.manager) },

	// --- Settings > Appearance ---
	[PERMISSION.SET_APPEARANCE]:          { nonPos: atLeast(ROLES.lead), pos: atLeast(ROLES.manager) },
	[PERMISSION.SET_LOCATION_IMAGE]:      { nonPos: atLeast(ROLES.manager), pos: atLeast(ROLES.manager) },
	[PERMISSION.SET_THEME]:               { nonPos: atLeast(ROLES.manager), pos: atLeast(ROLES.manager) },
	[PERMISSION.SET_FONTS]:               { nonPos: atLeast(ROLES.manager), pos: atLeast(ROLES.manager) },
	[PERMISSION.SET_LANGUAGES]:           { nonPos: atLeast(ROLES.lead), pos: atLeast(ROLES.manager) },
	[PERMISSION.SET_MENU_DISPLAY]:        { nonPos: atLeast(ROLES.manager), pos: atLeast(ROLES.manager) },

	// --- Settings > Promos ---
	[PERMISSION.SET_PROMO_CODES]:         { nonPos: atLeast(ROLES.manager), pos: atLeast(ROLES.manager) },

	// --- Settings > Business settings (pos preserves current manager-level tab gate) ---
	[PERMISSION.SET_BUSINESS_SETTINGS]:   { nonPos: atLeast(ROLES.manager), pos: atLeast(ROLES.manager) },
	[PERMISSION.SET_SERVICE_CHARGE]:      { nonPos: atLeast(ROLES.manager), pos: atLeast(ROLES.manager) },
	[PERMISSION.SET_TAX_RATES_SETTINGS]:  { nonPos: atLeast(ROLES.manager), pos: atLeast(ROLES.manager) },
	[PERMISSION.SET_MANAGE_ACCOUNTING_CATEGORIES]: { nonPos: allOf(ROLES.enterprise), pos: atLeast(ROLES.manager) },
	[PERMISSION.SET_END_OF_SCHOOL_YEAR]:  { nonPos: allOf(ROLES.enterprise), pos: atLeast(ROLES.manager) },

	// --- Settings > Subsidies ---
	[PERMISSION.SET_SUBSIDIES]:           { nonPos: allOf(ROLES.enterprise), pos: atLeast(ROLES.manager) },

	// --- Settings > Credits ---
	[PERMISSION.SET_ASSIGN_CREDITS]:      { nonPos: allOf(ROLES.enterprise), pos: atLeast(ROLES.manager) },

	// --- Location list ---
	[PERMISSION.LOCATION_LIST]:           { nonPos: atLeast(ROLES.member), pos: atLeast(ROLES.member) },
	[PERMISSION.LOCATION_REFRESH_MENU]:   { nonPos: allOf(ROLES.enterprise), pos: allOf(ROLES.enterprise) },
	[PERMISSION.LOCATION_SELECTOR]:       { nonPos: allOf(ROLES.enterprise), pos: allOf(ROLES.enterprise) },

	// --- Route guards (not matrix rows) ---
	[PERMISSION.ROUTE_MENUS]:             { nonPos: atLeast(ROLES.member), pos: atLeast(ROLES.member) },
	[PERMISSION.ROUTE_SETTINGS]:          { nonPos: atLeast(ROLES.supervisor), pos: atLeast(ROLES.supervisor) },
	[PERMISSION.ROUTE_REPLICATE]:         { nonPos: atLeast(ROLES.supervisor), pos: NONE },
	[PERMISSION.ROUTE_ORDER_MANAGEMENT]:  { nonPos: atLeast(ROLES.member), pos: atLeast(ROLES.member) },
	[PERMISSION.ROUTE_DASHBOARD]:         { nonPos: atLeast(ROLES.supervisor), pos: atLeast(ROLES.supervisor) },
	[PERMISSION.ROUTE_CUSTOMIZE]:         { nonPos: atLeast(ROLES.manager), pos: atLeast(ROLES.manager) },
	[PERMISSION.ROUTE_ADVANCED]:          { nonPos: atLeast(ROLES.manager), pos: atLeast(ROLES.manager) },
	[PERMISSION.ROUTE_MENU_GROUPS]:       { nonPos: atLeast(ROLES.manager), pos: atLeast(ROLES.manager) },
	[PERMISSION.ROUTE_CREDITS]:           { nonPos: allOf(ROLES.enterprise), pos: atLeast(ROLES.manager) },
	[PERMISSION.ROUTE_SUBSIDIES]:         { nonPos: allOf(ROLES.enterprise), pos: atLeast(ROLES.manager) },
	[PERMISSION.ROUTE_EVENTS]:            { nonPos: atLeast(ROLES.manager), pos: atLeast(ROLES.manager) },
	[PERMISSION.ROUTE_INQUIRIES]:         { nonPos: atLeast(ROLES.lead), pos: atLeast(ROLES.lead) },
	[PERMISSION.ROUTE_CATERING_BEO]:      { nonPos: atLeast(ROLES.lead), pos: atLeast(ROLES.lead) },
	[PERMISSION.ROUTE_ENTERPRISE_MENU_PULL]: { nonPos: atLeast(ROLES.enterprise), pos: atLeast(ROLES.enterprise) },
	[PERMISSION.ROUTE_MULTI_LOCATION_SETTINGS]: { nonPos: atLeast(ROLES.enterprise), pos: atLeast(ROLES.enterprise) },
	[PERMISSION.ROUTE_MULTI_LOCATION_BULK_EDIT]: { nonPos: atLeast(ROLES.enterprise), pos: atLeast(ROLES.enterprise) },

	// --- Row actions (ActionSheet) — OUTSIDE the permission matrix ---
	// These keys are NOT rows in the role-permissions matrix. They exist only to preserve the
	// POS-integrated gating that previously lived on each ActionSheet action's `accessLevel`
	// (archive/publish/duplicate/move) = admin-only on POS.
	// nonPos tiers mirror the parent menu's edit functions (MENU_ARCHIVE/DUPLICATE/DELETE) so a
	// role that can't archive/duplicate/delete a menu also can't do so to a section or item.
	[PERMISSION.SECTION_ARCHIVE]:         { nonPos: atLeast(ROLES.lead), pos: NONE },
	[PERMISSION.SECTION_DUPLICATE]:       { nonPos: atLeast(ROLES.supervisor), pos: NONE },
	[PERMISSION.SECTION_MOVE]:            { nonPos: atLeast(ROLES.member), pos: NONE },
	[PERMISSION.SECTION_DELETE]:          { nonPos: atLeast(ROLES.manager), pos: atLeast(ROLES.enterprise) },
	[PERMISSION.ITEM_ARCHIVE]:            { nonPos: atLeast(ROLES.lead), pos: NONE },
	[PERMISSION.ITEM_DUPLICATE]:          { nonPos: atLeast(ROLES.supervisor), pos: NONE },
	[PERMISSION.ITEM_MOVE]:               { nonPos: atLeast(ROLES.member), pos: NONE },
	[PERMISSION.ITEM_DELETE]:             { nonPos: atLeast(ROLES.manager), pos: atLeast(ROLES.enterprise) },
	[PERMISSION.MODIFIER_GROUP_DELETE]:   { nonPos: atLeast(ROLES.manager), pos: NONE },

	// --- Missing from Excel (not yet in the source-of-truth permission matrix) ---
	// Tiers reflect product decisions on top of the hardcoded role checks they replaced:
	// invoice settings, integrations + add-open-item extended to lead; reporting portal opened to all.
	[PERMISSION.SET_INVOICE_SETTINGS]:    { nonPos: atLeast(ROLES.lead), pos: atLeast(ROLES.lead) },
	[PERMISSION.SET_PAYMENT_VALIDATION_RULES]: { nonPos: atLeast(ROLES.manager), pos: atLeast(ROLES.manager) },
	[PERMISSION.SET_INTEGRATIONS]:        { nonPos: atLeast(ROLES.lead), pos: atLeast(ROLES.lead) },
	[PERMISSION.REPORTING_PORTAL]:        { nonPos: atLeast(ROLES.member), pos: atLeast(ROLES.member) },
	[PERMISSION.ORDERS_STATUS_SCREEN]:    { nonPos: atLeast(ROLES.supervisor), pos: atLeast(ROLES.supervisor) },
	[PERMISSION.ORDER_CATERING_DISCOUNT]: { nonPos: atLeast(ROLES.manager), pos: atLeast(ROLES.manager) },
	[PERMISSION.ORDER_ITEM_LEVEL_DISCOUNT]: { nonPos: atLeast(ROLES.manager), pos: NONE },
	[PERMISSION.ORDER_ADD_OPEN_ITEM]:     { nonPos: atLeast(ROLES.lead), pos: NONE },
	[PERMISSION.SET_ORDER_PARAMETERS]:    { nonPos: atLeast(ROLES.supervisor), pos: atLeast(ROLES.supervisor) },
	[PERMISSION.SET_ANNOUNCEMENTS]:       { nonPos: atLeast(ROLES.supervisor), pos: atLeast(ROLES.supervisor) },
	[PERMISSION.SET_NOTIFICATION_SOUND]:  { nonPos: atLeast(ROLES.supervisor), pos: atLeast(ROLES.supervisor) },
	// Clear saved students & subsidies (EndOfSchoolYear). Preserves the prior admin+enterprise gate
	// (roleLevel <= enterprise) on both POS and non-POS. Same set both sides = no POS distinction.
	[PERMISSION.CLEAR_STUDENTS_SUBSIDIES]: { nonPos: allOf(ROLES.enterprise), pos: allOf(ROLES.enterprise) },

	// --- Session identity (staff/kiosk — NOT Excel matrix rows) ---
	// Identity rather than capability. SESSION_STAFF/KIOSK set `stopAdminBypass` so the resolver
	// skips the admin bypass for them automatically — admin is never a terminal. OPERATOR is the
	// "not a terminal" gate (enterprise→member) and resolves normally (admin bypass is correct).
	[PERMISSION.OPERATOR]:                { nonPos: atLeast(ROLES.member), pos: atLeast(ROLES.member) },
	[PERMISSION.SESSION_STAFF]:           { nonPos: allOf(ROLES.staff), pos: allOf(ROLES.staff), stopAdminBypass: true },
	[PERMISSION.SESSION_KIOSK]:           { nonPos: allOf(ROLES.kiosk), pos: allOf(ROLES.kiosk), stopAdminBypass: true },

	// --- Internal (NOT a matrix row) ---
	// atLeast(admin) resolves to an empty client-role set (admin is excluded from CLIENT_ROLE_LEVELS),
	// so only admin passes via the hasPermission.ts bypass → "admin only". Reads as intent vs. NONE's "blocked".
	[PERMISSION.ADMIN_ONLY]:              { nonPos: atLeast(ROLES.admin), pos: atLeast(ROLES.admin) }
};
